/**
 * Signing metadata schema (embedded in PDF as signing-metadata.json).
 */
export type SignerEntry = {
  email: string;
  name?: string;
  delegationAllowed?: boolean;
  domainRestricted?: boolean;
};

export type FieldDefinition = {
  id: string;
  page: number;
  type:
  | "signature"
  | "initials"
  | "name"
  | "text"
  | "date-now"
  | "date"
  | "checkbox"
  | "label";
  signerEmail?: string; // Link to signer by email
  signerIndex?: number; // Or by index in signers array (safer if email changes? no, email is identity)
  x: number;
  y: number;
  width: number;
  height: number;
  labelText?: string;
  optional?: boolean;
};

export type ServerAttestation = {
  signerEmail: string;
  signerKeyId: string;
  docHash: string;
  ip: string;
  location: string;
  timestamp: string;
  serverKeyId: string;
  witnessed: boolean; // server witnessed the event (does NOT verify identity)
  verified?: boolean; // deprecated — kept for backward compat with existing signed docs
  signature: string; // ECDSA P-256 base64-encoded signature
  userAgent?: string;
  clientSignatureValid?: boolean;
  registryVerified?: boolean;
  registryFingerprint?: string;
};

export type SignatureEntry = {
  email: string;
  keyId: string;
  signature: string;
  timestamp: string; // ISO 8601
  pin?: string;
  totp?: string;
  serverSignature?: ServerAttestation;
  sessionInput?: any;
  publicKey?: string; // Embedded public key for offline verification
  delegatedFrom?: string; // Original expected signer email if different from key email
};

export type SigningMetadata = {
  version: number;
  documentHash: string;
  createdAt: string; // ISO 8601
  signers: SignerEntry[];
  fields: FieldDefinition[];
  signatures: SignatureEntry[];
  creatorSignature?: {
    keyId: string;
    signature: string;
    serverSignature?: ServerAttestation;
    publicKey?: string;
  };
};

export const SIGNING_METADATA_FILENAME = "signing-metadata.json";

/**
 * Compute the deterministic string that the creator should sign.
 * Binds together: document hash + signer list + field definitions.
 *
 * This ensures the creator's PGP signature covers the entire document
 * configuration, not just the raw file hash. If anyone modifies the
 * signer list or field layout after creation, the creator signature
 * will fail verification.
 *
 * The output is JSON-stable (keys sorted) so the same inputs always
 * produce the same signing input regardless of property order.
 */
export function computeCreatorSigningInput(
  documentHash: string,
  signers: SignerEntry[],
  fields: FieldDefinition[],
): string {
  const canonical = {
    documentHash,
    fields: fields.map((f) => ({
      id: f.id,
      page: f.page,
      type: f.type,
      signerIndex: f.signerIndex,
      x: f.x,
      y: f.y,
      width: f.width,
      height: f.height,
    })),
    signers: signers.map((s) => ({
      email: s.email,
      name: s.name,
    })),
  };
  return JSON.stringify(canonical, Object.keys(canonical).sort());
}

export function createSigningMetadata(
  documentHash: string,
  signers: SignerEntry[],
  fields: FieldDefinition[] = [],
  signatures: SignatureEntry[] = [],
): SigningMetadata {
  return {
    version: 2,
    documentHash,
    createdAt: new Date().toISOString(),
    signers,
    fields,
    signatures,
  };
}
